import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format price in EUR
export function formatPrice(price) {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
}

// Format date in Italian
export function formatDate(dateString) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

// Format short date
export function formatShortDate(dateString) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

// Format time
export function formatTime(timeString) {
  return timeString;
}

// Get status label in Italian
export function getStatusLabel(status) {
  const labels = {
    'IN_ATTESA': 'In Attesa',
    'CONFERMATO': 'Confermato',
    'COMPLETATO': 'Completato',
    'ANNULLATO': 'Annullato',
    'PENDING': 'In Attesa',
    'PAID': 'Pagato',
    'FAILED': 'Fallito',
    'REFUNDED': 'Rimborsato',
    'DEMO': 'Demo',
    'ACTIVE': 'Attivo',
    'SUSPENDED': 'Sospeso',
    'DELETED': 'Eliminato',
  };
  return labels[status] || status;
}

// Get status color class
export function getStatusColor(status) {
  const colors = {
    'IN_ATTESA': 'pending',
    'CONFERMATO': 'confirmed',
    'COMPLETATO': 'completed',
    'ANNULLATO': 'cancelled',
    'PENDING': 'pending',
    'PAID': 'completed',
    'FAILED': 'cancelled',
    'DEMO': 'pending',
    'ACTIVE': 'completed',
    'SUSPENDED': 'cancelled',
  };
  return colors[status] || 'pending';
}

// Generate time slots
export function generateTimeSlots(startTime, endTime, intervalMinutes = 30) {
  const slots = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  let currentHour = startHour;
  let currentMin = startMin;
  
  while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
    const time = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
    slots.push(time);
    
    currentMin += intervalMinutes;
    if (currentMin >= 60) {
      currentHour += Math.floor(currentMin / 60);
      currentMin = currentMin % 60;
    }
  }
  
  return slots;
}

// Get day name in Italian
export function getDayName(dayIndex) {
  const days = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
  return days[dayIndex] || '';
}

// Parse JWT token
export function parseToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

// Check if user has role
export function hasRole(user, roles) {
  if (!user) return false;
  if (typeof roles === 'string') roles = [roles];
  return roles.includes(user.role);
}

// Module codes
export const MODULE_CODES = {
  BASE: 'BASE',
  SHOP: 'SHOP',
  REPORTS_ADV: 'REPORTS_ADV',
  STAFF_PRESENCE: 'STAFF_PRESENCE',
  WHATSAPP: 'WHATSAPP',
};

// Check if store has module
export function hasModule(modules, moduleCode) {
  return modules?.includes(moduleCode) || false;
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Local storage helpers
export const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Error removing from localStorage:', e);
    }
  },
};
